import logger from '@finn-no/fiaas-logger';
import type { GetServerSidePropsContext, NextPage } from 'next';
import { useConfig } from 'shared/config/ConfigContext';
import { Head } from 'shared/page/Head';
import { Main } from 'shared/page/Main';
import type { DeviceType } from 'shared/types';
import { Hotjar } from 'shared/util/hotjar';
import { fetchSearchId } from '@/api/apiInternal';
import { FavoriteApiProvider } from '@/context/FavoriteApiProvider';
import type { FhhUnleashToggles } from '@/domain';
import { ExperimentProvider } from '@/hooks/experiments';
import { useStoreExperimentVariant } from '@/hooks/useStoreExperimentVariant';
import { ToastProvider } from '@/scenes/resultPage/context/toast/ToastProvider';
import { ResultPage } from '@/scenes/resultPage/ResultPage';
import { clientConfig, config } from '@/server/config';
import { buildCommonProps, sanitizeCommonProps } from '@/server/middleware';
import { fetchVariants } from '../../../../shared/frontend-resources/src/experiment/variants';

interface ResultatProps {
    deviceType: DeviceType;
    loginId: number;
    unleash: FhhUnleashToggles;
    hasLegalBasis: boolean;
    searchId: string | null;
    experiments: { [key: string]: string };
    source: TrafficSource | null;
}

type TrafficSource = 'request_rejected_system_message';

const Result: NextPage<ResultatProps> = (props) => {
    const { baseUrl } = useConfig();

    useStoreExperimentVariant('tjvr-breach-detection-in-boost', props.experiments?.['tjvr-breach-detection-in-boost'], 60);
    useStoreExperimentVariant('travel_traffic_source', props.source, 60);

    return (
        <ExperimentProvider experiments={props.experiments}>
            <Head
                title="Feriehjem og hytter - sammenlign pris | FINN reise"
                description="Finn tusenvis av feriehjem og hytter på et sted!"
                canonical={`${baseUrl}/reise/feriehus-hytteutleie/resultat/`}
            />
            <Main broadcastUrl="/broadcasts?path=www.finn.no/reise/feriehus-hytteutleie">
                <FavoriteApiProvider loginId={props.loginId}>
                    <ToastProvider>
                        <ResultPage {...props} searchId={props.searchId} />
                    </ToastProvider>
                </FavoriteApiProvider>
            </Main>
            <Hotjar enabled={Boolean(props.unleash?.hotjarResultList && props.hasLegalBasis)} />
        </ExperimentProvider>
    );
};

export const getServerSideProps = async ({ req, res, query }: GetServerSidePropsContext) => {
    const commonProps = await buildCommonProps(req, res, query);

    const experiments =
        (await fetchVariants(req, res, 'travel-fhh-web', config.get('experimentsApiUrl'), ['tjvr-breach-detection-in-boost'])) ?? {};
    const source = query.travel_traffic_source === 'request_rejected_system_message' ? 'request_rejected_system_message' : null;

    let searchId = null;

    if (query.booking_from && query.booking_to && query.no_of_beds_from) {
        const search = await fetchSearchId(query)
            .then((response) => response)
            .catch((err) => logger.error(err));
        searchId = search.searchId;
    }

    return {
        props: { config: clientConfig, ...sanitizeCommonProps(commonProps), searchId: searchId ?? null, experiments: experiments, source },
    };
};

export default Result;
